import React from 'react';
import { useTranslate, useDeleteMany, useMany } from "@pankod/refine-core";

import {
  useTable,
  List,
  Table,
  DateField,
  useModal,
  Button,
  Space,
  CreateButton,
  ShowButton,
  EditButton,
  DeleteButton,
  Modal,
  Typography,
  Checkbox,
  FilterDropdown,
  Radio,
  getDefaultSortOrder,
  useSelect,
  TextField,
  Select,
  BooleanField,
  Icons,
  Tag,
} from "@pankod/refine-antd";

import { IReview, IHospital } from 'interfaces';

export const ReviewList = () => {
  const translate = useTranslate();
  const { tableProps, sorter, tableQueryResult } = useTable<IReview>({
    initialSorter: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  });

  const defaultColumns = React.useMemo(
    () => [
      {
        label: translate('reviews.fields.id'),
        value: 'id',
      },
      {
        label: translate('reviews.fields.hospital.title'),
        value: 'hospital_id',
      },
      {
        label: translate('reviews.fields.is_deprecated'),
        value: 'is_deprecated',
      },
      {
        label: translate('reviews.fields.created_at'),
        value: 'created_at',
      },
      {
        label: translate('reviews.fields.updated_at'),
        value: 'updated_at',
      },
    ],
    [],
  );
  const { modalProps: columnsModalProps, show: columnsShow } = useModal();
  const hopsitalIds = tableProps?.dataSource?.map(item => item.hospital_id) ?? [];
  const { data: hospitalData, isLoading } = useMany<IHospital>({
    resource: 'hospitals',
    ids: hopsitalIds,
    queryOptions: {
      enabled: hopsitalIds.length > 0,
    },
  });
  const { selectProps: hospitalSelectProps } = useSelect<IHospital>({
    resource: 'hospitals',
    optionLabel: 'denomination',
    optionValue: 'id',
  });
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(['created_at', 'updated_at', 'id']);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const { mutate, isSuccess, isLoading: deleteManyIsLoading } = useDeleteMany<IReview>();
  const deleteSelectedItems = () => {
    mutate({
      resource: 'reviews',
      ids: selectedRowKeys.map(String),
    });
  };
  const onSelectChange = (selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
  };
  const hasSelected = selectedRowKeys.length > 0;
  const handleHideColumn = (value: string, isChecked: boolean) => {
    if (isChecked) setHiddenColumns(prev => prev.filter(col => col !== value));
    else setHiddenColumns(prev => [...prev, value]);
  };
  const columns = React.useMemo(
    () => [
      <Table.Column<IReview>
        key='id'
        dataIndex='id'
        title={translate('reviews.fields.id')}
        sorter
        defaultSortOrder={getDefaultSortOrder('id', sorter)}
      />,
      <Table.Column<IReview>
        key='is_deprecated'
        dataIndex='is_deprecated'
        title={translate('reviews.fields.is_deprecated')}
        render={value => (
          <BooleanField
            color={value ? '#f5222d' : '#52c41a'}
            value={value}
            trueIcon={<Icons.CheckCircleTwoTone style={{ fontSize: 18 }} twoToneColor='#f5222d' />}
            falseIcon={<Icons.CloseCircleTwoTone style={{ fontSize: 18 }} twoToneColor='#52c41a' />}
            valueLabelFalse={translate('no')}
            valueLabelTrue={translate('yes')}
          />
        )}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Radio.Group>
              <Radio value='true'>{translate('yes')}</Radio>
              <Radio value='false'>{translate('no')}</Radio>
            </Radio.Group>
          </FilterDropdown>
        )}
      />,
      <Table.Column
        key='hospital_id'
        dataIndex='hospital_id'
        title={translate('reviews.fields.hospital.title')}
        render={(value: string) => {
          if (isLoading) {
            return <TextField value={translate('loading')} />;
          }
          return <TextField value={hospitalData?.data.find((item: IHospital) => item.id === value)?.denomination} />;
        }}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Select
              mode='multiple'
              style={{ minWidth: 200 }}
              placeholder={translate('reviews.fields.hospital.filters.placeholder')}
              {...hospitalSelectProps}
            />
          </FilterDropdown>
        )}
      />,
      <Table.Column<IReview>
        key='created_at'
        dataIndex='created_at'
        title={translate('reviews.fields.created_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
      <Table.Column<IReview>
        key='updated_at'
        dataIndex='updated_at'
        title={translate('reviews.fields.updated_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
    ],
    [tableProps, sorter, hospitalData, hospitalSelectProps, isLoading],
  );
  const visibleColumns = React.useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.props.dataIndex));
  }, [hiddenColumns, columns]);

  React.useEffect(() => {
    if (isSuccess) {
      setSelectedRowKeys([]);
    }
  }, [isSuccess]);

  return (
    <>
      <List
        pageHeaderProps={{
          subTitle: hasSelected && (
            <Button danger onClick={deleteSelectedItems} disabled={!hasSelected} loading={deleteManyIsLoading}>
              {`Elimina ${selectedRowKeys.length} oggetti selezionati`}
            </Button>
          ),
          extra: (
            <Space>
              <Tag color='#f1c531'>
                {translate('labels.totalNumber', { resource: translate('reviews.reviews').toLowerCase() })}
                {tableQueryResult.data?.total || ''}
              </Tag>
              <CreateButton />
              <Button onClick={columnsShow}>{translate('table.hideColumns.buttonTitle')}</Button>
            </Space>
          ),
        }}>
        <Table<IReview>
          rowSelection={rowSelection}
          {...tableProps}
          size='small'
          scroll={{ y: '60vh', x: 10 * visibleColumns.length + 'vw' }}
          pagination={{ ...tableProps.pagination, position: ['topLeft'], size: 'small' }}
          rowKey='id'>
          <Table.Column<IReview>
            title=''
            dataIndex='actions'
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton title={translate('buttons.show')} size='small' recordItemId={record.id} hideText />
                  <EditButton title={translate('buttons.edit')} size='small' recordItemId={record.id} hideText />
                  <DeleteButton title={translate('buttons.delete')} size='small' recordItemId={record.id} hideText />
                </Space>
              );
            }}
          />
          {visibleColumns}
        </Table>
      </List>
      <Modal footer={null} {...columnsModalProps}>
        <Space direction='vertical'>
          <Typography.Text>{translate('table.hideColumns.title')}</Typography.Text>
          {defaultColumns.map(column => (
            <Checkbox
              key={column.value}
              checked={!hiddenColumns.includes(column.value)}
              onChange={e => handleHideColumn(column.value, e.target.checked)}>
              {column.label}
            </Checkbox>
          ))}
        </Space>
      </Modal>
    </>
  );
};
